import React, { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { ArrowRight, ArrowLeft, Clock, CalendarDays, ChevronRight } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Seo, SITE_URL, SITE_NAME } from '../components/Seo';
import { LOGO_URL } from '../data/mock';
import { getPost, getRelatedPosts } from '../data/blogPosts';
import { CATEGORY_STYLES } from './BlogIndexPage';
import { ExpertBadge } from '../components/ExpertBadge';

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

const mdComponents = {
  h2: ({ ...p }) => <h2 className="text-2xl font-black text-gray-900 mt-10 mb-3" {...p} />,
  h3: ({ ...p }) => <h3 className="text-xl font-bold text-gray-900 mt-8 mb-2" {...p} />,
  p: ({ ...p }) => <p className="text-[15px] sm:text-base text-gray-700 leading-relaxed mb-4" {...p} />,
  ul: ({ ...p }) => <ul className="list-disc pl-6 space-y-2 mb-4 text-gray-700" {...p} />,
  ol: ({ ...p }) => <ol className="list-decimal pl-6 space-y-2 mb-4 text-gray-700" {...p} />,
  li: ({ ...p }) => <li className="text-[15px] sm:text-base leading-relaxed" {...p} />,
  a: ({ ...p }) => <a className="text-cyo-orange font-semibold underline hover:text-orange-600" {...p} />,
  strong: ({ ...p }) => <strong className="font-bold text-gray-900" {...p} />,
  blockquote: ({ ...p }) => (
    <blockquote className="border-l-4 border-cyo-orange bg-orange-50/50 pl-4 py-2 my-4 text-gray-600 italic rounded-r-lg" {...p} />
  ),
  table: ({ ...p }) => (
    <div className="overflow-x-auto my-5">
      <table className="w-full text-sm border border-gray-200 rounded-lg" {...p} />
    </div>
  ),
  th: ({ ...p }) => <th className="bg-gray-50 text-left font-bold text-gray-900 px-3 py-2 border-b border-gray-200" {...p} />,
  td: ({ ...p }) => <td className="px-3 py-2 border-b border-gray-100 text-gray-700" {...p} />,
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = getPost(slug);

  useEffect(() => {
    if (post) window.scrollTo(0, 0);
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const style = CATEGORY_STYLES[post.category] || CATEGORY_STYLES.Guides;
  const Icon = style.icon;
  const url = `${SITE_URL}/blog/${post.slug}`;
  const ogImage = post.heroImage || LOGO_URL;
  const related = getRelatedPosts(post);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.metaDescription,
      image: ogImage,
      datePublished: post.datePublished,
      dateModified: post.dateModified || post.datePublished,
      author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
      publisher: {
        '@type': 'Organization',
        name: SITE_NAME,
        logo: { '@type': 'ImageObject', url: LOGO_URL },
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      keywords: (post.tags || []).join(', '),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
        { '@type': 'ListItem', position: 3, name: post.title, item: url },
      ],
    },
  ];

  if (post.faqs && post.faqs.length) {
    jsonLd.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: post.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title={post.seoTitle || post.title}
        description={post.metaDescription}
        path={`/blog/${post.slug}`}
        image={ogImage}
        type="article"
        jsonLd={jsonLd}
      />
      <Header />

      <main>
        {/* Hero */}
        <section className={`relative bg-gradient-to-br ${style.gradient} text-white overflow-hidden`} data-testid="blog-post-page">
          {post.heroImage && (
            <img src={post.heroImage} alt={post.title} className="absolute inset-0 w-full h-full object-cover opacity-30" />
          )}
          <div className="relative max-w-3xl mx-auto px-4 py-12 sm:py-16">
            <nav className="flex items-center gap-1.5 text-xs text-white/80 mb-5">
              <Link to="/" className="hover:text-white">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/blog" className="hover:text-white">Blog</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-white">{post.category}</span>
            </nav>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider bg-white/15 border border-white/25 px-3 py-1 rounded-full">
              <Icon className="w-3.5 h-3.5" /> {post.category}
            </span>
            <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-black leading-tight tracking-tight">
              {post.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-white/90">
              <span>{post.author}</span>
              <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />{fmtDate(post.datePublished)}</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" />{post.readMinutes} min read</span>
            </div>
          </div>
        </section>

        {/* Body */}
        <article className="max-w-3xl mx-auto px-4 py-10 sm:py-14">
          <div className="mb-6 pb-6 border-b border-gray-100">
            <ExpertBadge variant="light" align="left" />
          </div>
          <p className="text-lg text-gray-600 leading-relaxed mb-8 font-medium">{post.excerpt}</p>
          <ReactMarkdown components={mdComponents}>{post.body}</ReactMarkdown>

          {/* FAQs */}
          {post.faqs && post.faqs.length > 0 && (
            <div className="mt-12 pt-8 border-t border-gray-200" data-testid="blog-post-faqs">
              <h2 className="text-2xl font-black text-gray-900 mb-5">Frequently asked questions</h2>
              <div className="space-y-3">
                {post.faqs.map((f, i) => (
                  <div key={i} className="bg-gray-50 border border-gray-100 rounded-2xl p-5">
                    <h3 className="font-bold text-gray-900">{f.q}</h3>
                    <p className="mt-2 text-sm text-gray-600 leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Inline CTA */}
          <div className="mt-12 rounded-3xl bg-gray-950 text-white p-6 sm:p-8 text-center">
            <h2 className="text-2xl font-black">See what you could save</h2>
            <p className="mt-2 text-gray-300 text-sm sm:text-base max-w-lg mx-auto">
              Compare solar, battery, heat pumps and more for your home — free and no obligation.
            </p>
            <Link
              to="/compare"
              data-testid="blog-post-cta"
              className="mt-5 inline-flex items-center justify-center gap-2 bg-cyo-orange hover:bg-orange-600 text-white font-bold px-7 py-4 rounded-full transition-all"
            >
              Compare My Options <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <Link to="/blog" className="mt-8 inline-flex items-center gap-1.5 text-sm font-bold text-cyo-orange">
            <ArrowLeft className="w-4 h-4" /> Back to all articles
          </Link>
        </article>

        {/* Related */}
        {related.length > 0 && (
          <section className="bg-gray-50 py-12 sm:py-16 border-t border-gray-100">
            <div className="max-w-5xl mx-auto px-4">
              <h2 className="text-2xl font-black text-gray-900 mb-6">Keep reading</h2>
              <div className="grid sm:grid-cols-3 gap-5">
                {related.map((rp) => {
                  const rStyle = CATEGORY_STYLES[rp.category] || CATEGORY_STYLES.Guides;
                  const RIcon = rStyle.icon;
                  return (
                    <Link
                      key={rp.slug}
                      to={`/blog/${rp.slug}`}
                      data-testid={`blog-related-${rp.slug}`}
                      className="group block bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg transition-all"
                    >
                      <div className={`h-28 bg-gradient-to-br ${rStyle.gradient} flex items-center justify-center`}>
                        <RIcon className="w-9 h-9 text-white/90" />
                      </div>
                      <div className="p-4">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-cyo-orange">{rp.category}</span>
                        <h3 className="mt-1 font-bold text-gray-900 leading-snug group-hover:text-cyo-orange transition-colors">
                          {rp.title}
                        </h3>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default BlogPostPage;
